import { HttpApiService } from "@/shared/services/httpApi.service";
import { PUBLIC_ACCOUNTS_API_ENDPOINTS } from "../constants/public-accounts.constants";
import type {
  PublicAccountsListResponse,
  PublicAccountDetail,
  PublicAccountCopyStatsResponse,
} from "../types/public-accounts-api.types";
import type { NewPublicAccountFormData } from "../types/new-public-account.types";
import { buildFormDataForPublicAccount } from "../utils/buildFormDataForPublicAccount";

export interface FetchPublicAccountsParams {
  skip?: number;
  limit?: number;
  search?: string;
}

export interface CreatePublicAccountResponse {
  id?: string;
  [key: string]: unknown;
}

export const publicAccountsService = {
  create: async (
    data: NewPublicAccountFormData
  ): Promise<CreatePublicAccountResponse> => {
    const formData = buildFormDataForPublicAccount(data);
    const response = await HttpApiService.post<
      CreatePublicAccountResponse,
      FormData
    >(PUBLIC_ACCOUNTS_API_ENDPOINTS.LIST, formData);
    return response.data;
  },

  fetchList: async (
    params?: FetchPublicAccountsParams
  ): Promise<PublicAccountsListResponse> => {
    const path = PUBLIC_ACCOUNTS_API_ENDPOINTS.LIST;
    const queryParams: string[] = [];
    if (params?.skip != null) queryParams.push(`skip=${params.skip}`);
    if (params?.limit != null) queryParams.push(`limit=${params.limit}`);
    if (
      params?.search != null &&
      typeof params.search === "string" &&
      params.search.trim() !== ""
    ) {
      queryParams.push(`search=${encodeURIComponent(params.search.trim())}`);
    }
    const url =
      queryParams.length > 0 ? `${path}?${queryParams.join("&")}` : path;
    const response = await HttpApiService.get<PublicAccountsListResponse>(url);
    return response.data;
  },

  fetchById: async (id: string): Promise<PublicAccountDetail> => {
    const url = PUBLIC_ACCOUNTS_API_ENDPOINTS.getById(id);
    const response = await HttpApiService.get<PublicAccountDetail>(url);
    return response.data;
  },

  copy: async (id: string): Promise<PublicAccountDetail> => {
    const url = PUBLIC_ACCOUNTS_API_ENDPOINTS.copy(id);
    const response = await HttpApiService.post<PublicAccountDetail, Record<string, never>>(url, {});
    return response.data;
  },

  update: async (
    id: string,
    data: NewPublicAccountFormData
  ): Promise<PublicAccountDetail> => {
    const formData = buildFormDataForPublicAccount(data);
    const url = PUBLIC_ACCOUNTS_API_ENDPOINTS.getById(id);
    const response = await HttpApiService.patch<PublicAccountDetail, FormData>(
      url,
      formData
    );
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    const url = PUBLIC_ACCOUNTS_API_ENDPOINTS.getById(id);
    await HttpApiService.delete<void>(url);
  },

  fetchCopyStats: async (id: string): Promise<PublicAccountCopyStatsResponse> => {
    const url = PUBLIC_ACCOUNTS_API_ENDPOINTS.getCopyStats(id);
    const response = await HttpApiService.get<PublicAccountCopyStatsResponse>(url);
    return response.data;
  },
};
